<template>
  <div class="theme-toggle">
    <se-button
      type="button"
      variant="text"
      style-set="set8"
      @click="toggleTheme"
      class="theme-toggle-btn"
      :class="[{ 'animating': animating }, size]"
      :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
      :aria-pressed="isDark"
    >
      <div class="btn-background"></div>
      <div class="icon-container">
        <v-icon
          :icon="isDark ? 'mdi-weather-sunny' : 'mdi-weather-night'"
          size="20"
          class="theme-icon"
          :class="{ 'rotate-icon': animating }"
        />
      </div>
      <div class="theme-indicator" :class="{ 'dark': isDark }"></div>
    </se-button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onMounted } from 'vue'
import { useTheme } from 'vuetify'
import SeButton from '@/components/SEButton.vue'

// Prefer destructure default values; keeps reactive destructure enabled
const { size = 'medium' } = defineProps<{
  size?: 'small' | 'medium'
}>()

const theme = useTheme()
const animating = ref(false)
let transitionFrame: number | null = null

const isDark = computed(() => theme.global.current.value.dark)

const suppressThemeTransitions = () => {
  const root = document.documentElement
  root.classList.add('theme-switching')

  if (transitionFrame !== null) {
    window.cancelAnimationFrame(transitionFrame)
  }

  transitionFrame = window.requestAnimationFrame(() => {
    transitionFrame = window.requestAnimationFrame(() => {
      root.classList.remove('theme-switching')
      transitionFrame = null
    })
  })
}

const toggleTheme = () => {
  animating.value = true
  suppressThemeTransitions()

  // Toggle between light and dark themes
  const newTheme = isDark.value ? 'light' : 'dark'
  theme.global.name.value = newTheme

  // Apply CSS class for theme-specific styling
  if (newTheme === 'light') {
    document.documentElement.classList.add('theme-light')
  } else {
    document.documentElement.classList.remove('theme-light')
  }

  // Save preference to localStorage
  localStorage.setItem('theme-preference', newTheme)

  // Add haptic feedback if available
  if ('vibrate' in navigator) {
    navigator.vibrate(50)
  }

  // Reset animation state
  setTimeout(() => {
    animating.value = false
  }, 700)
}

// Load saved theme preference
onMounted(() => {
  const savedTheme = localStorage.getItem('theme-preference')
  if (savedTheme && ['light', 'dark'].includes(savedTheme)) {
    theme.global.name.value = savedTheme
    // Apply CSS class for theme-specific styling
    if (savedTheme === 'light') {
      document.documentElement.classList.add('theme-light')
    } else {
      document.documentElement.classList.remove('theme-light')
    }
  } else {
    // Sync with system preference if no saved preference
    syncWithSystem()
  }
})

// Sync with system preference
const syncWithSystem = () => {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const systemTheme = prefersDark ? 'dark' : 'light'
  theme.global.name.value = systemTheme

  // Apply CSS class for theme-specific styling
  if (systemTheme === 'light') {
    document.documentElement.classList.add('theme-light')
  } else {
    document.documentElement.classList.remove('theme-light')
  }
}

// Listen for system theme changes
onMounted(() => {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

  const handleSystemThemeChange = (e: MediaQueryListEvent) => {
    // Only auto-sync if user hasn't set a preference
    if (!localStorage.getItem('theme-preference')) {
      const systemTheme = e.matches ? 'dark' : 'light'
      theme.global.name.value = systemTheme

      // Apply CSS class for theme-specific styling
      if (systemTheme === 'light') {
        document.documentElement.classList.add('theme-light')
      } else {
        document.documentElement.classList.remove('theme-light')
      }
    }
  }

  mediaQuery.addEventListener('change', handleSystemThemeChange)

  // Cleanup listener on unmount
  return () => {
    mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }
})

onBeforeUnmount(() => {
  if (transitionFrame !== null) {
    window.cancelAnimationFrame(transitionFrame)
  }
  document.documentElement.classList.remove('theme-switching')
})
</script>

<style scoped>
* {
  font-family: var(--font-family-body);
}

.theme-toggle {
  position: relative;
  display: flex;
  align-items: center;
}

.theme-toggle-btn {
  position: relative;
  width: 44px;
  height: 44px;
  border: 1px solid transparent;
  border-radius: var(--radius-lg);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.theme-toggle-btn:hover {
  background: transparent;
  border-color: transparent;
  transform: none;
  box-shadow: none;
}

.theme-toggle-btn:active {
  transform: translateY(0) scale(0.95);
}

.theme-toggle-btn.animating {
  animation: pulse 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.btn-background {
  position: absolute;
  inset: 0;
  background: transparent;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: var(--radius-lg);
}

.theme-toggle-btn:hover .btn-background {
  opacity: 0;
}

.icon-container {
  position: relative;
  z-index: var(--z-control);
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme-icon {
  color: var(--text-body);
  transition: all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.theme-toggle-btn:hover .theme-icon {
  color: var(--accent);
}

.rotate-icon {
  transform: rotate(360deg) scale(1.1);
}

.theme-indicator {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--text-primary) 30%, transparent);
  transition: all 0.3s ease;
  z-index: var(--z-content);
}

.theme-indicator.dark {
  background: var(--accent);
  box-shadow: var(--shadow-accent-glow);
}

/* Enhanced tooltip styling */
:global(.v-tooltip .v-overlay__content) {
  background: var(--bg-dark) !important;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  font-size: var(--font-size-caption);
  font-weight: 500;
  padding: 8px 12px;
  color: color-mix(in srgb, var(--text-inverse) 90%, transparent);
  box-shadow: var(--shadow-modal);
}

/* Global theme transition improvements */
:global(.v-application) {
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:global(.v-application .v-card) {
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:global(.v-application .v-app-bar) {
  transition: background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:global(.v-application .v-btn) {
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              color 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Accessibility improvements */
.theme-toggle-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* Reduced motion — STRENGTHENED (was: transition-duration: 0.1s — still caused motion) */
@media (prefers-reduced-motion: reduce) {
  .theme-icon,
  .theme-toggle-btn,
  .btn-background,
  .theme-indicator {
    transition: none !important;
  }

  .rotate-icon {
    transform: rotate(0deg) scale(1);
  }

  .theme-toggle-btn.animating {
    animation: none;
  }

  /* Only suppress what ThemeToggle's own :global() rules introduce.
     .v-btn is excluded — Vuetify 3.3+ handles it natively. */
  :global(.v-application),
  :global(.v-application .v-card),
  :global(.v-application .v-app-bar) {
    transition: none !important;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .theme-toggle-btn {
    border: 2px solid currentColor;
  }

  .theme-icon {
    color: currentColor;
  }
}

/* Mobile touch improvements */
@media (hover: none) and (pointer: coarse) {
  .theme-toggle-btn {
    width: 48px;
    height: 48px;
  }

  .theme-toggle-btn:hover {
    transform: none;
  }

  .theme-toggle-btn:active {
    background: rgba(var(--accent-rgb), 0.2);
    transform: scale(0.95);
  }
}

/* Size variants */
.theme-toggle-btn.small {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
}

.theme-toggle-btn.small .theme-icon {
  font-size: var(--font-size-md) !important;
}

.theme-toggle-btn.small .theme-indicator {
  width: 4px;
  height: 4px;
  right: 3px;
  bottom: 3px;
}
</style>
